import { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, isTouchDevice, Modal, TextField } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import {
  createUserNameSchemaWithTranslations,
  MAX_NAME_LENGTH,
  type NameChangeRequestPayload,
  useUpdateName,
} from "../../hooks/useUpdateName";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";

import style from "./NameChangeModal.module.css";

type NameChangeFormData = {
  name: string;
};

export default function NameChangeModal({
  open = true,
  onClose,
  onNameChangeSuccess,
  name,
}: {
  open?: boolean;
  onClose: () => void;
  onNameChangeSuccess?: () => void;
  name?: string;
}) {
  const { t, m } = useTranslation();
  const schema = useMemo(() => z.object({ name: createUserNameSchemaWithTranslations(t) }), [t]);
  const tracker = useTracker();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<NameChangeFormData>({
    resolver: zodResolver(schema),
    defaultValues: { name },
  });

  useEffect(() => {
    if (open) {
      reset({ name });
    }
  }, [open, name, reset]);

  const { mutateAsync, error } = useUpdateName({
    onSuccess: () => {
      onClose();
      onNameChangeSuccess?.();
    },
  });

  const submit = async (data: NameChangeRequestPayload) => {
    try {
      await mutateAsync(data);
    } catch (error) {
      // This is to not throw Unhandled Promise Rejection error
    }
  };

  let errorMessage = null;
  if (error) {
    errorMessage = t(m.ErrorMessage.Unknown);
  }

  useEffect(() => {
    if (errors.name) {
      tracker.trackNameChangeModalError(errors.name?.type);
    }
  }, [errors, tracker]);

  return (
    <Modal open={open} title={"Edit your name"} size={"small"} onClose={onClose}>
      <form className={style.nameChangeForm} onSubmit={handleSubmit(submit)}>
        <TextField
          {...register("name")}
          placeholder="Your name"
          errorMessage={errors.name?.message}
          maxLength={MAX_NAME_LENGTH}
          disabled={isSubmitting}
          autoFocus={!isTouchDevice()}
        ></TextField>
        {errorMessage && <div className={style.error}>{errorMessage}</div>}
        <div className={style.buttonContainer}>
          <Button
            type="submit"
            className={classNames(style.fullWidth)}
            onClick={() => {
              tracker.trackButtonClick({
                location: ElementClickLocation.CHANGE_NAME_MODAL,
                action: ElementClickAction.CONFIRM,
              });
            }}
            disabled={isSubmitting}
          >
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
